import {
  useMutation,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  checklistCreateBodySchema,
  checklistHideCheckedPutSchema,
  checklistItemAssigneePatchSchema,
  checklistItemCheckedPatchSchema,
  checklistItemConvertToCardBodySchema,
  checklistItemCreateBodySchema,
  checklistItemDueDatePatchSchema,
  checklistItemListSchema,
  checklistItemMoveBodySchema,
  checklistItemSchema,
  checklistItemTextPatchSchema,
  checklistListSchema,
  checklistMoveBodySchema,
  checklistSchema,
  checklistTitlePatchSchema,
  type BoardContents,
  type BoardMember,
  type CardDetail,
  type Checklist,
  type ChecklistCreateBody,
  type ChecklistHideCheckedPut,
  type ChecklistItem,
  type ChecklistItemAssigneePatch,
  type ChecklistItemCheckedPatch,
  type ChecklistItemConvertToCardBody,
  type ChecklistItemCreateBody,
  type ChecklistItemDueDatePatch,
  type ChecklistItemList,
  type ChecklistItemMoveBody,
  type ChecklistItemTextPatch,
  type ChecklistList,
  type ChecklistMoveBody,
  type ChecklistTitlePatch,
  parseMentions,
} from '@molt/shared';
import { announce } from './announce';
import { del, emptyResponseSchema, get, patch, post, put } from './api';
import { applyCardDetailToBoardContents } from './cards';
import { setBoardContentsQueries } from './cross-board-move';
import { queryKeys } from './query-keys';

/**
 * FE-80.1 -- the translator every announcing checklist hook takes.
 *
 * Structurally identical to `InterfaceLanguageContextValue['t']` and to
 * `CommentTranslate` in `lib/comments.ts`. It is a **required parameter**
 * rather than a `useInterfaceLanguage()` call inside each hook on purpose: the
 * context resolves against an English fallback catalogue when no provider is
 * mounted, so a hook that read it itself would render English with nothing
 * red. Taking it as an argument makes `tsc` enumerate every call site.
 */
export type ChecklistTranslate = (
  key: string,
  values?: Record<string, string | number>,
  count?: number,
) => string;

export interface ChecklistMutationVariables {
  readonly cardId: string;
  readonly boardId: string;
}

export interface CreateChecklistVariables extends ChecklistMutationVariables {
  readonly title: string;
  readonly sourceChecklistId?: string | null | undefined;
}

export interface RenameChecklistVariables extends ChecklistMutationVariables {
  readonly checklistId: string;
  readonly title: string;
}

export interface MoveChecklistVariables extends ChecklistMutationVariables {
  readonly checklistId: string;
  readonly previousChecklistId: string | null;
  readonly nextChecklistId: string | null;
}

export interface DeleteChecklistVariables extends ChecklistMutationVariables {
  readonly checklistId: string;
  readonly title: string;
}

export interface SetChecklistHideCheckedVariables extends ChecklistMutationVariables {
  readonly checklistId: string;
  readonly hidden: boolean;
}

export interface AddChecklistItemsVariables extends ChecklistMutationVariables {
  readonly checklistId: string;
  readonly text: string;
}

export interface RenameChecklistItemVariables extends ChecklistMutationVariables {
  readonly itemId: string;
  readonly text: string;
}

export interface SetChecklistItemCheckedVariables extends ChecklistMutationVariables {
  readonly itemId: string;
  readonly checked: boolean;
}

export interface SetChecklistItemAssigneeVariables extends ChecklistMutationVariables {
  readonly itemId: string;
  readonly assigneeUserId: string | null;
}

export interface SetChecklistItemDueDateVariables extends ChecklistMutationVariables {
  readonly itemId: string;
  readonly dueAt: string | null;
}

export interface ConvertChecklistItemToCardVariables extends ChecklistMutationVariables {
  readonly itemId: string;
  readonly listId?: string | null | undefined;
  readonly previousCardId?: string | null | undefined;
  readonly nextCardId?: string | null | undefined;
}

export interface MoveChecklistItemVariables extends ChecklistMutationVariables {
  readonly itemId: string;
  readonly checklistId: string;
  readonly previousItemId: string | null;
  readonly nextItemId: string | null;
}

export interface DeleteChecklistItemVariables extends ChecklistMutationVariables {
  readonly itemId: string;
}

export interface ChecklistProgress {
  readonly total: number;
  readonly checked: number;
  readonly hidden: number;
  readonly percent: number;
  readonly complete: boolean;
}

export interface ChecklistCopySourceOption {
  readonly id: string;
  readonly label: string;
  readonly itemCount: number;
}

export type ChecklistMentionSegment =
  | { readonly kind: 'text'; readonly text: string }
  | { readonly kind: 'mention'; readonly text: string; readonly member: BoardMember };

export function checklistMentionSegments(
  text: string,
  members: readonly BoardMember[],
): readonly ChecklistMentionSegment[] {
  const mentionedHandles = new Set(parseMentions(text));
  if (mentionedHandles.size === 0 || members.length === 0) {
    return text === '' ? [] : [{ kind: 'text', text }];
  }

  const membersByUsername = new Map(
    members.map((member) => [member.username.toLowerCase(), member]),
  );
  const segments: ChecklistMentionSegment[] = [];
  const mentionPattern = /(^|[^A-Za-z0-9._/-])@([A-Za-z0-9][A-Za-z0-9._-]*)/g;
  let cursor = 0;
  let match: RegExpExecArray | null;

  while ((match = mentionPattern.exec(text)) !== null) {
    const prefix = match[1] ?? '';
    const handle = match[2] ?? '';
    const username = trimMentionHandle(handle).toLowerCase();
    const member =
      username === '' || !mentionedHandles.has(username)
        ? undefined
        : membersByUsername.get(username);

    if (member === undefined) continue;

    const start = match.index + prefix.length;
    const end = start + 1 + username.length;
    if (start > cursor) segments.push({ kind: 'text', text: text.slice(cursor, start) });
    segments.push({ kind: 'mention', text: text.slice(start, end), member });
    cursor = end;
  }

  if (cursor < text.length) segments.push({ kind: 'text', text: text.slice(cursor) });
  return segments.length === 0 ? [{ kind: 'text', text }] : segments;
}

export function getCardChecklists(cardId: string): Promise<ChecklistList> {
  return get(`/cards/${encodeURIComponent(cardId)}/checklists`, checklistListSchema);
}

export function createChecklist(
  cardId: string,
  body: ChecklistCreateBody,
): Promise<Checklist> {
  return post(
    `/cards/${encodeURIComponent(cardId)}/checklists`,
    checklistSchema,
    checklistCreateBodySchema.parse(body),
  );
}

export function renameChecklist(
  checklistId: string,
  body: ChecklistTitlePatch,
): Promise<Checklist> {
  return patch(
    `/checklists/${encodeURIComponent(checklistId)}/title`,
    checklistSchema,
    checklistTitlePatchSchema.parse(body),
  );
}

export function moveChecklist(
  checklistId: string,
  body: ChecklistMoveBody,
): Promise<Checklist> {
  return post(
    `/checklists/${encodeURIComponent(checklistId)}/move`,
    checklistSchema,
    checklistMoveBodySchema.parse(body),
  );
}

export function deleteChecklist(checklistId: string): Promise<null> {
  return del(`/checklists/${encodeURIComponent(checklistId)}`, emptyResponseSchema);
}

export function setChecklistHideChecked(
  checklistId: string,
  body: ChecklistHideCheckedPut,
): Promise<Checklist> {
  return put(
    `/checklists/${encodeURIComponent(checklistId)}/hide-checked`,
    checklistSchema,
    checklistHideCheckedPutSchema.parse(body),
  );
}

export function addChecklistItems(
  checklistId: string,
  body: ChecklistItemCreateBody,
): Promise<ChecklistItemList> {
  return post(
    `/checklists/${encodeURIComponent(checklistId)}/items`,
    checklistItemListSchema,
    checklistItemCreateBodySchema.parse(body),
  );
}

export function renameChecklistItem(
  itemId: string,
  body: ChecklistItemTextPatch,
): Promise<ChecklistItem> {
  return patch(
    `/checklist-items/${encodeURIComponent(itemId)}/text`,
    checklistItemSchema,
    checklistItemTextPatchSchema.parse(body),
  );
}

export function setChecklistItemChecked(
  itemId: string,
  body: ChecklistItemCheckedPatch,
): Promise<ChecklistItem> {
  return patch(
    `/checklist-items/${encodeURIComponent(itemId)}/checked`,
    checklistItemSchema,
    checklistItemCheckedPatchSchema.parse(body),
  );
}

export function setChecklistItemAssignee(
  itemId: string,
  body: ChecklistItemAssigneePatch,
): Promise<ChecklistItem> {
  return patch(
    `/checklist-items/${encodeURIComponent(itemId)}/assignee`,
    checklistItemSchema,
    checklistItemAssigneePatchSchema.parse(body),
  );
}

export function setChecklistItemDueDate(
  itemId: string,
  body: ChecklistItemDueDatePatch,
): Promise<ChecklistItem> {
  return patch(
    `/checklist-items/${encodeURIComponent(itemId)}/due-date`,
    checklistItemSchema,
    checklistItemDueDatePatchSchema.parse(body),
  );
}

export function convertChecklistItemToCard(
  itemId: string,
  body: ChecklistItemConvertToCardBody,
): Promise<ChecklistItem> {
  return post(
    `/checklist-items/${encodeURIComponent(itemId)}/convert-to-card`,
    checklistItemSchema,
    checklistItemConvertToCardBodySchema.parse(body),
  );
}

export function moveChecklistItem(
  itemId: string,
  body: ChecklistItemMoveBody,
): Promise<ChecklistItem> {
  return post(
    `/checklist-items/${encodeURIComponent(itemId)}/move`,
    checklistItemSchema,
    checklistItemMoveBodySchema.parse(body),
  );
}

export function deleteChecklistItem(itemId: string): Promise<null> {
  return del(`/checklist-items/${encodeURIComponent(itemId)}`, emptyResponseSchema);
}

export function checklistProgress(checklist: Checklist): ChecklistProgress {
  const total = checklist.items.length;
  const checked = checklist.items.filter((item) => item.checked).length;
  const hidden = checklist.hideChecked ? checked : 0;
  return {
    total,
    checked,
    hidden,
    percent: total === 0 ? 0 : Math.round((checked / total) * 100),
    complete: total > 0 && checked === total,
  };
}

export function visibleChecklistItems(checklist: Checklist): readonly ChecklistItem[] {
  if (!checklist.hideChecked) return checklist.items;
  return checklist.items.filter((item) => !item.checked);
}

export function copySourceOptions(
  checklists: readonly Checklist[],
): ChecklistCopySourceOption[] {
  return checklists.map((checklist) => ({
    id: checklist.id,
    label: checklist.title,
    itemCount: checklist.items.length,
  }));
}

export function mentionSegments(
  text: string,
  members: readonly BoardMember[],
): readonly ChecklistMentionSegment[] {
  return checklistMentionSegments(text, members);
}

export function applyChecklistToCardDetail(
  previous: CardDetail | undefined,
  next: Checklist,
): CardDetail | undefined {
  if (previous === undefined) return undefined;
  const exists = previous.checklists.some((checklist) => checklist.id === next.id);
  const checklists = exists
    ? previous.checklists.map((checklist) =>
        checklist.id === next.id ? next : checklist,
      )
    : [...previous.checklists, next];
  return { ...previous, checklists: sortChecklists(checklists) };
}

export function applyChecklistMutationToCardDetail(
  previous: CardDetail | undefined,
  next: Checklist,
): CardDetail | undefined {
  return applyChecklistToCardDetail(previous, next);
}

export function applyChecklistItemsToCardDetail(
  previous: CardDetail | undefined,
  checklistId: string,
  items: readonly ChecklistItem[],
): CardDetail | undefined {
  if (previous === undefined || items.length === 0) return previous;
  return {
    ...previous,
    checklists: previous.checklists.map((checklist) => {
      if (checklist.id !== checklistId) return checklist;
      const ids = new Set(items.map((item) => item.id));
      return {
        ...checklist,
        items: sortItems([
          ...checklist.items.filter((item) => !ids.has(item.id)),
          ...items,
        ]),
      };
    }),
  };
}

export function removeChecklistFromCardDetail(
  previous: CardDetail | undefined,
  checklistId: string,
): CardDetail | undefined {
  if (previous === undefined) return undefined;
  return {
    ...previous,
    checklists: previous.checklists.filter((checklist) => checklist.id !== checklistId),
  };
}

export function removeChecklistItemFromCardDetail(
  previous: CardDetail | undefined,
  itemId: string,
): CardDetail | undefined {
  if (previous === undefined) return undefined;
  return {
    ...previous,
    checklists: previous.checklists.map((checklist) => ({
      ...checklist,
      items: checklist.items.filter((item) => item.id !== itemId),
    })),
  };
}

export function moveChecklistItemInCardDetail(
  previous: CardDetail | undefined,
  item: ChecklistItem,
): CardDetail | undefined {
  if (previous === undefined) return undefined;
  return {
    ...previous,
    checklists: previous.checklists.map((checklist) => {
      const withoutItem = checklist.items.filter((candidate) => candidate.id !== item.id);
      if (checklist.id !== item.checklistId) return { ...checklist, items: withoutItem };
      return { ...checklist, items: sortItems([...withoutItem, item]) };
    }),
  };
}

export function applyChecklistItemToCardDetail(
  previous: CardDetail | undefined,
  item: ChecklistItem,
): CardDetail | undefined {
  return moveChecklistItemInCardDetail(previous, item);
}

export function applyCardChecklistCountsToBoardContents(
  previous: BoardContents | undefined,
  detail: CardDetail,
): BoardContents | undefined {
  return applyCardDetailToBoardContents(previous, detail);
}

export function useCreateChecklist(
  t: ChecklistTranslate,
): UseMutationResult<Checklist, unknown, CreateChecklistVariables> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables) =>
      createChecklist(variables.cardId, {
        title: variables.title,
        sourceChecklistId: variables.sourceChecklistId ?? null,
      }),
    onSuccess: (checklist, variables) => {
      updateCardDetail(queryClient, variables, (previous) =>
        applyChecklistToCardDetail(previous, checklist),
      );
      announce(t('cardBack.checklists.announce.added', { checklist: checklist.title }));
    },
    onError: () => announce(t('cardBack.checklists.announce.addFailed')),
  });
}

export function useRenameChecklist(
  t: ChecklistTranslate,
): UseMutationResult<Checklist, unknown, RenameChecklistVariables> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables) =>
      renameChecklist(variables.checklistId, { title: variables.title }),
    onSuccess: (checklist, variables) => {
      updateCardDetail(queryClient, variables, (previous) =>
        applyChecklistToCardDetail(previous, checklist),
      );
      announce(t('cardBack.checklists.announce.renamed', { checklist: checklist.title }));
    },
    onError: () => announce(t('cardBack.checklists.announce.renameFailed')),
  });
}

export function useMoveChecklist(
  t: ChecklistTranslate,
): UseMutationResult<Checklist, unknown, MoveChecklistVariables> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables) =>
      moveChecklist(variables.checklistId, {
        previousChecklistId: variables.previousChecklistId,
        nextChecklistId: variables.nextChecklistId,
      }),
    onSuccess: (checklist, variables) => {
      updateCardDetail(queryClient, variables, (previous) =>
        applyChecklistToCardDetail(previous, checklist),
      );
    },
    onError: () => announce(t('cardBack.checklists.announce.moveFailed')),
  });
}

export function useDeleteChecklist(
  t: ChecklistTranslate,
): UseMutationResult<null, unknown, DeleteChecklistVariables> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables) => deleteChecklist(variables.checklistId),
    onSuccess: (_deleted, variables) => {
      updateCardDetail(queryClient, variables, (previous) =>
        removeChecklistFromCardDetail(previous, variables.checklistId),
      );
      announce(t('cardBack.checklists.announce.deleted', { checklist: variables.title }));
    },
    onError: () => announce(t('cardBack.checklists.announce.deleteFailed')),
  });
}

export function useSetChecklistHideChecked(
  t: ChecklistTranslate,
): UseMutationResult<Checklist, unknown, SetChecklistHideCheckedVariables> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables) =>
      setChecklistHideChecked(variables.checklistId, { hidden: variables.hidden }),
    onSuccess: (checklist, variables) => {
      updateCardDetail(queryClient, variables, (previous) =>
        applyChecklistToCardDetail(previous, checklist),
      );
    },
    onError: () => announce(t('cardBack.checklists.announce.hideCheckedFailed')),
  });
}

export function useAddChecklistItems(
  t: ChecklistTranslate,
): UseMutationResult<ChecklistItemList, unknown, AddChecklistItemsVariables> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables) =>
      addChecklistItems(variables.checklistId, { text: variables.text }),
    onSuccess: (items, variables) => {
      updateCardDetail(queryClient, variables, (previous) =>
        applyChecklistItemsToCardDetail(previous, variables.checklistId, items),
      );
      announce(
        t(
          'cardBack.checklists.announce.itemsAdded',
          { count: items.length },
          items.length,
        ),
      );
    },
    onError: () => announce(t('cardBack.checklists.announce.itemAddFailed')),
  });
}

export function useRenameChecklistItem(
  t: ChecklistTranslate,
): UseMutationResult<ChecklistItem, unknown, RenameChecklistItemVariables> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables) =>
      renameChecklistItem(variables.itemId, { text: variables.text }),
    onSuccess: (item, variables) => {
      updateCardDetail(queryClient, variables, (previous) =>
        moveChecklistItemInCardDetail(previous, item),
      );
      void queryClient.invalidateQueries({ queryKey: queryKeys.meHome() });
    },
    onError: () => announce(t('cardBack.checklists.announce.itemRenameFailed')),
  });
}

export function useSetChecklistItemChecked(
  t: ChecklistTranslate,
): UseMutationResult<ChecklistItem, unknown, SetChecklistItemCheckedVariables> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables) =>
      setChecklistItemChecked(variables.itemId, { checked: variables.checked }),
    onSuccess: (item, variables) => {
      updateCardDetail(queryClient, variables, (previous) =>
        moveChecklistItemInCardDetail(previous, item),
      );
      void queryClient.invalidateQueries({ queryKey: queryKeys.meHome() });
    },
    onError: () => announce(t('cardBack.checklists.announce.itemUpdateFailed')),
  });
}

export function useSetChecklistItemAssignee(
  t: ChecklistTranslate,
): UseMutationResult<ChecklistItem, unknown, SetChecklistItemAssigneeVariables> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables) =>
      setChecklistItemAssignee(variables.itemId, {
        assigneeUserId: variables.assigneeUserId,
      }),
    onSuccess: (item, variables) => {
      updateChecklistItemCaches(queryClient, variables, item);
      void queryClient.invalidateQueries({ queryKey: queryKeys.meCards() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.meHome() });
      announce(
        item.assigneeUserId === null
          ? t('cardBack.checklists.announce.itemAssignmentCleared')
          : t('cardBack.checklists.announce.itemAssigned'),
      );
    },
    onError: () => announce(t('cardBack.checklists.announce.itemAssignFailed')),
  });
}

export function useSetChecklistItemDueDate(
  t: ChecklistTranslate,
): UseMutationResult<ChecklistItem, unknown, SetChecklistItemDueDateVariables> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables) =>
      setChecklistItemDueDate(variables.itemId, { dueAt: variables.dueAt }),
    onSuccess: (item, variables) => {
      updateChecklistItemCaches(queryClient, variables, item);
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.detail(variables.boardId),
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.meCards() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.meHome() });
      announce(
        item.dueAt === null
          ? t('cardBack.checklists.announce.itemDueCleared')
          : t('cardBack.checklists.announce.itemDueSet'),
      );
    },
    onError: () => announce(t('cardBack.checklists.announce.itemDueFailed')),
  });
}

export function useConvertChecklistItemToCard(
  t: ChecklistTranslate,
): UseMutationResult<ChecklistItem, unknown, ConvertChecklistItemToCardVariables> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables) =>
      convertChecklistItemToCard(variables.itemId, {
        listId: variables.listId ?? null,
        previousCardId: variables.previousCardId ?? null,
        nextCardId: variables.nextCardId ?? null,
      }),
    onSuccess: (item, variables) => {
      const sourceListId = queryClient.getQueryData<CardDetail>(
        queryKeys.cards.detail(variables.cardId),
      )?.listId;
      updateChecklistItemCaches(queryClient, variables, item);
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.detail(variables.boardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.contents(variables.boardId),
      });
      const destinationListId = variables.listId ?? sourceListId;
      if (destinationListId !== undefined && destinationListId !== null) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.lists.cards(destinationListId),
        });
      }
      void queryClient.invalidateQueries({ queryKey: queryKeys.meCards() });
      announce(
        item.convertedCard === null
          ? t('cardBack.checklists.announce.itemConverted')
          : t('cardBack.checklists.announce.itemConvertedNamed', {
              card: item.convertedCard.title,
            }),
      );
    },
    onError: () => announce(t('cardBack.checklists.announce.itemConvertFailed')),
  });
}

export function useMoveChecklistItem(
  t: ChecklistTranslate,
): UseMutationResult<ChecklistItem, unknown, MoveChecklistItemVariables> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables) =>
      moveChecklistItem(variables.itemId, {
        checklistId: variables.checklistId,
        previousItemId: variables.previousItemId,
        nextItemId: variables.nextItemId,
      }),
    onSuccess: (item, variables) => {
      updateCardDetail(queryClient, variables, (previous) =>
        moveChecklistItemInCardDetail(previous, item),
      );
    },
    onError: () => announce(t('cardBack.checklists.announce.itemMoveFailed')),
  });
}

export function useDeleteChecklistItem(
  t: ChecklistTranslate,
): UseMutationResult<null, unknown, DeleteChecklistItemVariables> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables) => deleteChecklistItem(variables.itemId),
    onSuccess: (_deleted, variables) => {
      updateCardDetail(queryClient, variables, (previous) =>
        removeChecklistItemFromCardDetail(previous, variables.itemId),
      );
    },
    onError: () => announce(t('cardBack.checklists.announce.itemDeleteFailed')),
  });
}

function updateChecklistItemCaches(
  queryClient: QueryClient,
  variables: ChecklistMutationVariables,
  item: ChecklistItem,
): void {
  updateCardDetail(queryClient, variables, (previous) =>
    applyChecklistItemToCardDetail(previous, item),
  );
}

function updateCardDetail(
  queryClient: QueryClient,
  variables: ChecklistMutationVariables,
  updater: (previous: CardDetail | undefined) => CardDetail | undefined,
): void {
  const previous = queryClient.getQueryData<CardDetail>(
    queryKeys.cards.detail(variables.cardId),
  );
  const next = updater(previous);
  if (next === undefined) {
    void queryClient.invalidateQueries({
      queryKey: queryKeys.cards.detail(variables.cardId),
    });
    void queryClient.invalidateQueries({
      queryKey: queryKeys.cards.checklists(variables.cardId),
    });
    void queryClient.invalidateQueries({
      queryKey: queryKeys.boards.contents(variables.boardId),
    });
    return;
  }
  queryClient.setQueryData(queryKeys.cards.detail(variables.cardId), next);
  queryClient.setQueryData(queryKeys.cards.checklists(variables.cardId), next.checklists);
  setBoardContentsQueries(queryClient, variables.boardId, (contents) =>
    applyCardDetailToBoardContents(contents, next),
  );
}

function sortChecklists(checklists: readonly Checklist[]): Checklist[] {
  return [...checklists].sort((a, b) => compareRanked(a, b));
}

function sortItems(items: readonly ChecklistItem[]): ChecklistItem[] {
  return [...items].sort((a, b) => compareRanked(a, b));
}

function compareRanked(
  a: { readonly position: string; readonly id: string },
  b: { readonly position: string; readonly id: string },
): number {
  if (a.position < b.position) return -1;
  if (a.position > b.position) return 1;
  if (a.id < b.id) return -1;
  if (a.id > b.id) return 1;
  return 0;
}

function trimMentionHandle(handle: string): string {
  return handle.replace(/[._-]+$/u, '');
}
